import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import Reveal from "@/components/Reveal";

export default function About() {
  const { t } = useLang();

  return (
    <div data-testid="about-page" className="bg-maly-cream">
      {/* Hero */}
      <section className="pt-40 lg:pt-48 pb-20 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-8">
              {t.about.overline}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-light tracking-[-0.02em] leading-[1.02] max-w-5xl">
              {t.about.hero}
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="font-serif italic text-xl lg:text-2xl opacity-70 mt-8 max-w-2xl leading-relaxed">{t.about.sub}</p>
          </Reveal>
        </div>
      </section>

      {/* Editorial image */}
      <section className="px-6 lg:px-12 mb-32">
        <Reveal className="max-w-[1400px] mx-auto aspect-[16/9] bg-maly-creamDeep img-zoom">
          <img
            src="https://images.pexels.com/photos/37990557/pexels-photo-37990557.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
        </Reveal>
      </section>

      {/* Mission */}
      <section className="py-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <Reveal>
              <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-6">
                {t.about.missionTitle}
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-4xl lg:text-5xl font-light leading-[1.05]">
                {t.about.missionTitle}.
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-7 lg:col-start-6">
            <ul className="space-y-8">
              {t.about.mission.map((m, i) => (
                <Reveal key={i} delay={i * 100}>
                  <li className="flex gap-6 pb-8 border-b border-maly-navy/10">
                    <span className="font-display text-2xl text-maly-gold mt-1 flex-shrink-0">0{i + 1}</span>
                    <p className="font-display text-2xl lg:text-3xl leading-snug font-light tracking-tight">{m}</p>
                  </li>
                </Reveal>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Story */}
      <section className="py-24 px-6 lg:px-12 bg-white">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <Reveal className="lg:col-span-5 order-2 lg:order-1">
            <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-6">
              {t.about.storyTitle}
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-light leading-[1.05] mb-8">{t.about.storyTitle}</h2>
            <p className="font-body text-base lg:text-lg leading-[1.85] opacity-75">{t.about.storyBody}</p>
            <Link to="/shop" data-testid="about-shop-cta" className="btn-primary mt-10">
              {t.common.shopCollection} <ArrowRight size={14} />
            </Link>
          </Reveal>
          <Reveal delay={150} className="lg:col-span-7 order-1 lg:order-2">
            <div className="aspect-[4/5] img-zoom bg-maly-creamDeep">
              <img
                src="https://images.unsplash.com/photo-1500932334442-8761ee4810a7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxnb2xmfGVufDB8fHx8MTc4MjY1MzcyM3ww&ixlib=rb-4.1.0&q=85"
                alt=""
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values navy */}
      <section className="py-32 lg:py-40 bg-maly-navy text-maly-cream px-6 lg:px-12 relative grain">
        <div className="max-w-[1400px] mx-auto relative">
          <Reveal>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-60 mb-6">
              {t.about.valuesTitle}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl lg:text-6xl font-light max-w-3xl leading-[1.05]">{t.valuesSection.headline}</h2>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-12 mt-20">
            {t.valuesSection.values.map((v, i) => (
              <Reveal key={i} delay={i * 100}>
                <div>
                  <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-gold mb-5">0{i + 1}</div>
                  <h3 className="font-display text-2xl mb-4 font-light">{v.title}</h3>
                  <p className="font-body text-sm opacity-70 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
